// 레이드 바닥 연출(RAID-PRD 5절): 남는 불 장판과 비룡이 노리는 파티원 발밑 표식.
// 불 장판(2026-10-27 raid-vfx): 살아 있는 용암. InstancedMesh 1개(알파 혼합 + HDR 색이라 뜨거운 곳만 블룸). 흐르는 사인 잡음으로 굳은 껍질 판과
// 그 사이 빛나는 틈을 나누고, 가장자리는 일렁이는 밝은 테 + 바깥 옅은 열빛. 수명에 따라 식는다: 갓 생김(흰 노랑) → 주황 → 마지막 1.5초 검붉게 굳으며 옅어짐.
// 불씨·거품 터짐은 raidVfx.ts가 활성 장판 위에서 낸다.
// 대상 표식: 끊긴 이중 고리 + 안쪽을 가리키는 화살 셋(반대로 돈다), 박동. 붉은색(도발 중이면 방패병 파랑). 프레임마다 행렬·속성 값만 바꾼다(할당 없음). 섬광 없음.
import * as THREE from 'three/webgpu';
import { abs, atan, attribute, float, fract, max, mix, sin, smoothstep, uniform, uv, vec2, vec3 } from 'three/tsl';
import { WYVERN } from '../shared/content/raid';
import type { GameView } from '../shared/types';

const FADE_TICKS = 60;
/** 굳기 시작(남은 틱) */
const COOL_TICKS = 90;
/** 판 크기 배율(바깥 열빛까지 그리려고 판정 지름보다 크게 그린다 — 용암 가장자리는 EDGE에서 판정 반경과 맞다) */
const HALO = 1.18;
const EDGE = 1 / HALO;

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

function fireMaterial(t: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  m.forceSinglePass = true;
  // aFire = (옅어짐 0..1, 열 0..1, 씨앗, 나이 초)
  const F: N = attribute('aFire', 'vec4');
  const fade = F.x, heat = F.y, seed = F.z, age = F.w;
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  const a = atan(p.y, p.x);
  // 가장자리가 일렁이는 원(반경이 각도·시간에 따라 흔들린다). 갓 생긴 장판은 0.25초 동안 퍼진다
  const spread = smoothstep(float(0), float(0.25), age).mul(0.25).add(0.75);
  const wob = sin(a.mul(7).add(t.mul(2.2)).add(seed.mul(9))).mul(0.03).add(sin(a.mul(13).sub(t.mul(3.1))).mul(0.018)).add(sin(a.mul(3).add(t.mul(0.9))).mul(0.02));
  const edge = float(EDGE).add(wob).mul(spread);
  const inside = float(1).sub(smoothstep(edge.sub(0.035), edge, r));
  // 흐르는 용암: 두 겹 사인 잡음. 높은 곳 = 굳은 껍질 판, 낮은 곳 = 빛나는 틈
  const q = p.add(vec2(seed.mul(7), seed.mul(3)));
  const n1 = sin(q.x.mul(5.5).add(t.mul(0.45)).add(sin(q.y.mul(4.3).sub(t.mul(0.35))).mul(1.3)))
    .mul(sin(q.y.mul(5.1).sub(t.mul(0.4)).add(sin(q.x.mul(3.7).add(t.mul(0.3))).mul(1.3))));
  const n2 = sin(q.x.add(q.y).mul(10.5).sub(t.mul(1.1))).mul(0.35).add(sin(q.x.sub(q.y).mul(8.3).add(t.mul(0.8))).mul(0.25));
  const lava = n1.mul(0.7).add(n2);
  // 굳은 껍질 판 사이로 가는 용암 줄기(잡음의 0 근처 = 그물 같은 금)가 빛나고, 뜨거울수록 줄기가 굵고 녹은 웅덩이가 보인다
  const vein = float(1).sub(smoothstep(float(0), mix(float(0.06), float(0.2), heat), lava.abs()));
  const molten = smoothstep(float(0.5), float(0.95), lava).mul(heat);
  const seam = max(vein, molten);
  // 가장자리 밝은 테(바깥 0.2) — 일렁이는 용암 둑
  const rim = smoothstep(edge.sub(0.24), edge.sub(0.03), r).mul(inside);
  // 바깥 열빛
  const halo = smoothstep(edge.add(0.16), edge, r).mul(float(1).sub(inside)).mul(0.45);
  // 채도를 지키려고 HDR을 낮게(블룸에서 흰 크림색으로 날아가지 않게 — 2026-10-27 캡처). 갓 생긴 장판만 노랗게 밝다
  const hotA = vec3(1.9, 0.75, 0.12), hotB = vec3(1.45, 0.3, 0.03), cold = vec3(0.65, 0.07, 0.015);
  const hot = mix(cold, mix(hotB, hotA, heat.sub(0.6).max(0).mul(2.5)), heat.mul(0.85).add(0.15));
  const crustCol = mix(vec3(0.035, 0.012, 0.01), vec3(0.16, 0.025, 0.008), heat.mul(0.7));
  const flick = sin(t.mul(7).add(a.mul(5)).add(r.mul(9))).mul(0.12).add(0.88);
  const body = mix(crustCol, hot.mul(flick), max(seam, rim.mul(0.9)));
  m.colorNode = mix(body, hot.mul(0.8), float(1).sub(inside));
  m.opacityNode = inside.mul(0.94).add(halo).mul(fade).clamp(0, 1);
  return m;
}

function markMaterial(col: N, t: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  const a = atan(p.y, p.x);
  // 바깥 고리: 네 토막(회전은 인스턴스 행렬)
  const ring = smoothstep(0.74, 0.8, r).mul(float(1).sub(smoothstep(0.9, 0.96, r)));
  const gap = smoothstep(0.08, 0.16, abs(fract(a.div(Math.PI / 2)).sub(0.5)));
  // 안쪽 가는 고리(반대로 도는 점선)
  const a2 = a.add(t.mul(2.6));
  const dots = smoothstep(0.25, 0.4, abs(fract(a2.div(Math.PI / 8)).sub(0.5)));
  const inner = smoothstep(0.56, 0.6, r).mul(float(1).sub(smoothstep(0.63, 0.67, r))).mul(dots);
  // 안쪽을 가리키는 화살 셋(고리 사이), 박동하며 안으로 들어온다
  // 갈매기 꼭짓점이 안쪽(r 작은 쪽)을 향한다: r - r0 = |s|·k 선
  const s3 = fract(a.sub(t.mul(1.3)).div(Math.PI * 2 / 3)).sub(0.5).abs().mul(Math.PI * 2 / 3);
  const push = sin(t.mul(6)).mul(0.03);
  const arrowLine = abs(r.sub(float(0.6).sub(push)).sub(s3.mul(0.85)));
  const arrow = float(1).sub(smoothstep(float(0.025), float(0.05), arrowLine)).mul(float(1).sub(smoothstep(float(0.2), float(0.24), s3)));
  // 옅은 면
  const fill = smoothstep(0.8, 0.0, r).mul(0.12);
  m.colorNode = col;
  m.opacityNode = ring.mul(gap).mul(0.95).add(inner.mul(0.7)).add(arrow.mul(0.9)).add(fill).clamp(0, 1);
  return m;
}

export class RaidFx {
  readonly group = new THREE.Group();
  private readonly fires: THREE.InstancedMesh;
  private readonly mark: THREE.Mesh;
  private readonly markMat: THREE.MeshBasicNodeMaterial;
  private readonly time = uniform(0);
  private readonly markCol = uniform(new THREE.Color(0xff4a3a));
  private readonly mtx = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly pos = new THREE.Vector3();
  private readonly scl = new THREE.Vector3();
  private readonly aFire: THREE.InstancedBufferAttribute;
  private readonly flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  private readonly spin = new THREE.Quaternion();
  private readonly yAxis = new THREE.Vector3(0, 1, 0);

  constructor() {
    this.group.name = 'raid-fx';
    const cap = WYVERN.fire.max;
    const fg = new THREE.PlaneGeometry(1, 1);
    this.aFire = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    this.aFire.setUsage(THREE.DynamicDrawUsage);
    fg.setAttribute('aFire', this.aFire);
    this.fires = new THREE.InstancedMesh(fg, fireMaterial(this.time), cap);
    this.fires.name = 'fx-raid-fire';
    this.fires.count = 0;
    this.fires.frustumCulled = false;
    this.fires.renderOrder = 4;
    this.fires.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.markMat = markMaterial(this.markCol, this.time);
    const mg = new THREE.PlaneGeometry(1, 1);
    mg.rotateX(-Math.PI / 2);
    this.mark = new THREE.Mesh(mg, this.markMat);
    this.mark.name = 'fx-raid-target';
    this.mark.renderOrder = 4;
    this.mark.frustumCulled = false;
    this.mark.visible = false;
    this.group.add(this.fires, this.mark);
    this.group.visible = false;
  }

  /** timeSec = 세계 시계(초) */
  update(view: GameView, alpha: number, timeSec: number): void {
    const r = view.raid;
    if (!r || !r.active) { this.group.visible = false; return; }
    this.group.visible = true;
    this.time.value = timeSec;
    let n = 0;
    for (let i = 0; i < r.fires.length && n < this.fires.instanceMatrix.count; i++) {
      const f = r.fires[i];
      if (!f.active) continue;
      const d = f.radius * 2 * HALO;
      const grow = Math.min(1, (f.total - f.ttl) / 10 + 0.4);
      this.spin.setFromAxisAngle(this.yAxis, i * 1.3 + timeSec * 0.15);
      this.q.copy(this.spin).multiply(this.flat);
      this.pos.set(f.x, 0.05 + i * 0.002, f.z);
      this.scl.set(d * grow, d * grow, d * grow);
      this.mtx.compose(this.pos, this.q, this.scl);
      this.fires.setMatrixAt(n, this.mtx);
      const fade = Math.min(1, f.ttl / FADE_TICKS);
      const age = (f.total - f.ttl) / 60;
      // 열: 갓 생김 1 → 0.6초에 걸쳐 0.6 → 남은 COOL_TICKS부터 0으로 굳는다
      const heat = f.ttl < COOL_TICKS ? 0.6 * f.ttl / COOL_TICKS : 0.6 + 0.4 * Math.max(0, 1 - age / 0.6);
      const fa = this.aFire.array as Float32Array;
      fa[n * 4] = fade; fa[n * 4 + 1] = heat; fa[n * 4 + 2] = (i * 0.618) % 1; fa[n * 4 + 3] = age;
      n++;
    }
    this.fires.count = n;
    this.fires.visible = n > 0;
    if (n > 0) { this.fires.instanceMatrix.needsUpdate = true; this.aFire.needsUpdate = true; }

    // 대상 표식: 영웅(0) 또는 용병(1..3). 쓰러졌거나 없으면 숨긴다
    let tx = 0, tz = 0, show = false;
    const b = view.boss;
    const fighting = b.state !== 0 && b.hp > 0;
    if (fighting && r.target === 0) {
      const h = view.hero;
      tx = h.prevX + (h.x - h.prevX) * alpha; tz = h.prevZ + (h.z - h.prevZ) * alpha; show = h.hp > 0;
    } else if (fighting && r.target > 0) {
      const m = r.mercs[r.target - 1];
      if (m && m.down === 0) { tx = m.prevX + (m.x - m.prevX) * alpha; tz = m.prevZ + (m.z - m.prevZ) * alpha; show = true; }
    }
    this.mark.visible = show;
    if (show) {
      const pulse = 1 + 0.06 * Math.sin(timeSec * 6);
      this.mark.position.set(tx, 0.06, tz);
      this.mark.rotation.y = timeSec * 1.4;
      this.mark.scale.setScalar(2.1 * pulse);
      (this.markCol.value as THREE.Color).setHex(r.tauntTicks > 0 && r.target === 1 ? 0x6aa8ff : 0xff4a3a);
    }
  }

  prewarm(on: boolean): void {
    this.group.visible = on;
    if (on) { (this.aFire.array as Float32Array).set([1, 1, 0, 1], 0); this.fires.count = 1; this.fires.visible = true; this.mark.visible = true; } else { this.fires.count = 0; }
  }

  dispose(): void {
    this.fires.geometry.dispose();
    (this.fires.material as THREE.Material).dispose();
    this.fires.dispose();
    this.mark.geometry.dispose();
    this.markMat.dispose();
  }
}
